import JSBI from "jsbi";
import { ethers } from "ethers";
import { TickMath, encodeSqrtRatioX96, nearestUsableTick } from "@uniswap/v3-sdk";
import launchArtifact from "../../out/RevShareV4FeePolicyLaunch.sol/RevShareV4FeePolicyLaunch.json";
import { CHAINS, SHARED_DISTRIBUTOR } from "./config";

const UPLOAD_API_URL = import.meta.env.VITE_UPLOAD_API_URL || "https://app.revshare.ltd/api/upload-to-pinata";
const START_PRICE_NATIVE_PER_TOKEN = "0.00000000224";
const TICK_SPACING = 200;
const RANGE_TICKS = TICK_SPACING * 1000;
const POLICY_CONFIG = "tuple(uint16 developerBps,uint16 holdersBps,uint16 platformBps,uint16 burnBps,uint16 liquidityBps,address developer,address platform,address rewardAsset,address liquidityReceiver,uint8 holderRewardMode,uint16 lotteryWinnerCount,address randomnessProvider,uint32 lotteryRoundDuration,uint256 minimumLotteryPot)";
const V4_CONFIG = `tuple(string name,string symbol,uint8 decimals,uint256 totalSupply,string metadataURI,address poolManager,address owner,address feeCollector,address platformFeeWallet,uint256 platformFeeBps,uint24 lpFee,int24 tickSpacing,int24 tickLower,int24 tickUpper,uint160 sqrtPriceX96,uint128 liquidity,uint128 initialLiquidityNative,uint128 initialBuyNative,bytes32 positionSalt,${POLICY_CONFIG} policy,address converter,address curveReader,address weth,address distributionWallet,address holderRewardRouteAdapter,address factoryOwnerSource,uint256 graduationThreshold,address[] excludedAccounts)`;
const FACTORY_ABI = [
  "function v4FeePolicyLauncher() view returns(address)",
  "function createTokenV4(bytes encodedConfig) payable returns(address,address,address,address,bytes32)",
  "event FeePolicyV4Launched(address indexed caller,address indexed launchContract,address indexed token,address feeVault,address poolManager,bytes32 poolId)",
];
const LAUNCHER_ABI = ["function launchCreationCodeHash() view returns(bytes32)"];

function curve(totalSupply, decimals) {
  const nativePerToken = ethers.utils.parseUnits(START_PRICE_NATIVE_PER_TOKEN, 18);
  const oneToken = ethers.BigNumber.from(10).pow(decimals);
  const unitsPerNative = oneToken.mul(ethers.constants.WeiPerEther).div(nativePerToken);
  const sqrt = TickMath.getSqrtRatioAtTick(nearestUsableTick(
    TickMath.getTickAtSqrtRatio(JSBI.BigInt(encodeSqrtRatioX96(unitsPerNative.toString(), ethers.constants.WeiPerEther.toString()).toString())),
    TICK_SPACING,
  ));
  const current = nearestUsableTick(TickMath.getTickAtSqrtRatio(sqrt), TICK_SPACING);
  const tickUpper = current - TICK_SPACING;
  const tickLower = tickUpper - RANGE_TICKS;
  const lower = ethers.BigNumber.from(TickMath.getSqrtRatioAtTick(tickLower).toString());
  const upper = ethers.BigNumber.from(TickMath.getSqrtRatioAtTick(tickUpper).toString());
  return {
    sqrtPriceX96: sqrt.toString(), tickLower, tickUpper,
    liquidity: totalSupply.mul(ethers.BigNumber.from(2).pow(96)).div(upper.sub(lower)),
  };
}

export async function launchV4({ chainKey, walletClient, owner, form, onStatus }) {
  const chain = CHAINS[chainKey];
  if (!chain) throw new Error("Unsupported chain.");
  const provider = new ethers.providers.Web3Provider(walletClient);
  const network = await provider.getNetwork();
  if (network.chainId !== chain.chainId) throw new Error(`Switch your wallet to ${chain.name}.`);
  const signer = provider.getSigner();
  const signerAddress = await signer.getAddress();
  if (signerAddress.toLowerCase() !== owner.toLowerCase()) throw new Error("Connected wallet changed.");

  const decimals = 9;
  const totalSupply = ethers.utils.parseUnits("1000000000", decimals);
  const initialBuy = ethers.utils.parseEther(String(form.initialBuy || "0"));
  const walletBalance = await provider.getBalance(signerAddress);
  if (walletBalance.lte(initialBuy)) {
    throw new Error(
      `Insufficient ${chain.symbol} balance. Wallet has ${ethers.utils.formatEther(walletBalance)} ${chain.symbol}, but the initial buy alone requires ${ethers.utils.formatEther(initialBuy)} ${chain.symbol}, plus gas.`,
    );
  }
  const values = form.percentages;
  const total = Number(values.developer) + Number(values.holders) + Number(values.burn) + Number(values.liquidity);
  if (Math.abs(total - 100) > 0.001) throw new Error("Fee allocations must total exactly 100%.");
  const developerBps = Math.floor(Number(values.developer) * 100);
  const holdersBps = Math.floor(Number(values.holders) * 100);
  const burnBps = Math.floor(Number(values.burn) * 100);
  const liquidityBps = 10000 - developerBps - holdersBps - burnBps;
  const lottery = form.mode === "lottery";
  const rewardAsset = form.rewardAsset && ethers.utils.isAddress(form.rewardAsset) ? ethers.utils.getAddress(form.rewardAsset) : ethers.constants.AddressZero;
  const policy = {
    developerBps, holdersBps, platformBps: 0, burnBps, liquidityBps,
    developer: owner, platform: ethers.constants.AddressZero, rewardAsset,
    liquidityReceiver: liquidityBps ? owner : ethers.constants.AddressZero,
    holderRewardMode: lottery ? 1 : 0,
    lotteryWinnerCount: lottery ? Math.max(1, Math.min(100, Math.floor(Number(form.winners || 1)))) : 0,
    randomnessProvider: ethers.constants.AddressZero,
    lotteryRoundDuration: lottery ? Math.max(3600, Math.min(30 * 86400, Math.floor(Number(form.roundHours || 1)) * 3600)) : 0,
    minimumLotteryPot: lottery ? ethers.utils.parseEther(String(form.minimumPot || "0.0001")) : 0,
  };
  const price = curve(totalSupply, decimals);
  const factory = new ethers.Contract(chain.factory, FACTORY_ABI, signer);
  const creationCode = `0x${String(launchArtifact.bytecode?.object || "").replace(/^0x/, "")}`;
  if (creationCode === "0x") throw new Error("Bundled V4 launch bytecode is missing.");

  onStatus("Checking FactoryV2 launcher...");
  const launcherAddress = await factory.v4FeePolicyLauncher();
  const expectedHash = await new ethers.Contract(launcherAddress, LAUNCHER_ABI, provider).launchCreationCodeHash();
  if (ethers.utils.keccak256(creationCode) !== expectedHash) throw new Error(`${chain.name} FactoryV2 launcher does not match this static app build.`);
  const config = {
    name: form.name.trim(), symbol: form.symbol.trim().toUpperCase(), decimals, totalSupply, metadataURI: form.metadataURI,
    poolManager: ethers.constants.AddressZero, owner, feeCollector: SHARED_DISTRIBUTOR,
    platformFeeWallet: ethers.constants.AddressZero, platformFeeBps: 0,
    lpFee: Math.round(Number(form.poolFee) * 10000), tickSpacing: TICK_SPACING,
    tickLower: price.tickLower, tickUpper: price.tickUpper, sqrtPriceX96: price.sqrtPriceX96,
    liquidity: price.liquidity, initialLiquidityNative: 0, initialBuyNative: initialBuy,
    positionSalt: ethers.utils.hexlify(ethers.utils.randomBytes(32)), policy,
    converter: ethers.constants.AddressZero, curveReader: ethers.constants.AddressZero, weth: ethers.constants.AddressZero,
    distributionWallet: SHARED_DISTRIBUTOR, holderRewardRouteAdapter: ethers.constants.AddressZero,
    factoryOwnerSource: ethers.constants.AddressZero, graduationThreshold: ethers.utils.parseEther(chain.graduation), excludedAccounts: [],
  };
  const encoded = ethers.utils.defaultAbiCoder.encode(["bytes", V4_CONFIG], [creationCode, config]);
  onStatus("Estimating direct V4 launch...");
  let gas;
  try {
    gas = await factory.estimateGas.createTokenV4(encoded, { value: initialBuy });
  } catch (error) {
    if (error?.code === "INSUFFICIENT_FUNDS" || /insufficient funds/i.test(String(error?.message || ""))) {
      const gasPrice = await provider.getGasPrice().catch(() => ethers.constants.Zero);
      throw new Error(
        `Insufficient ${chain.symbol} for the initial buy and network gas. Wallet balance: ${ethers.utils.formatEther(walletBalance)} ${chain.symbol}; initial buy: ${ethers.utils.formatEther(initialBuy)} ${chain.symbol}; current gas price: ${ethers.utils.formatUnits(gasPrice, "gwei")} gwei.`,
      );
    }
    throw error;
  }
  onStatus("Confirm the FactoryV2 transaction in your wallet...");
  const tx = await factory.createTokenV4(encoded, { value: initialBuy, gasLimit: gas.mul(125).div(100) });
  onStatus("Waiting for on-chain confirmation...");
  const receipt = await tx.wait();
  const event = receipt.logs.map((log) => { try { return factory.interface.parseLog(log); } catch { return null; } })
    .find((entry) => entry?.name === "FeePolicyV4Launched");
  if (!event) throw new Error("Launch succeeded but the token event was not found.");
  return { token: ethers.utils.getAddress(event.args.token), launch: ethers.utils.getAddress(event.args.launchContract), poolId: event.args.poolId, tx: receipt.transactionHash };
}

export async function uploadMetadata({ file, name, symbol, description, website, twitter, telegram }) {
  if (!file) throw new Error("Choose a token logo.");
  const upload = async (body) => {
    let response;
    try {
      response = await fetch(UPLOAD_API_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    } catch {
      throw new Error("Could not reach the RevShare IPFS upload service. Ensure the upload API with CORS support is deployed.");
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.error || "IPFS metadata upload failed. Please try again.");
    return String(data.ipfsUrl || data.metadataUri || data.url || "");
  };
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const image = await upload({ type: "file", data: { base64, name: file.name, mimeType: file.type || "image/png" } });
  return upload({ type: "json", data: { name, symbol, description, image, external_url: website || "https://revshare.dev", properties: { category: "image", website, xAccount: twitter, telegram } } });
}
